import { Capacitor } from '@capacitor/core';
import { Haptics, ImpactStyle, NotificationType } from '@capacitor/haptics';
import { Keyboard } from '@capacitor/keyboard';
import { StatusBar, Style } from '@capacitor/status-bar';

// Native "feel" for the iOS/Android app (docs/design-system.md → Native layer):
// - haptics on tab switches, switches/segmented controls, primary actions and
//   success/error toasts,
// - the status bar text follows the light/dark theme,
// - the tab bar hides while the keyboard is open (body.keyboard-open).
// Feature scripts can call window.APP_NATIVE.haptic('success') directly. In the
// browser every call is a no-op.

type HapticKind = 'light' | 'medium' | 'heavy' | 'selection' | 'success' | 'warning' | 'error';

declare global {
  interface Window {
    APP_NATIVE?: { isNative: boolean; haptic(kind?: HapticKind): void };
  }
}

const native = Capacitor.isNativePlatform();
const available = (name: string) => native && Capacitor.isPluginAvailable(name);
const hapticsOn = available('Haptics');
let lastHaptic = 0;

function haptic(kind: HapticKind = 'light'): void {
  if (!hapticsOn) return;
  const now = Date.now();
  if (now - lastHaptic < 40) return; // one tap → one tick, even if several handlers fire
  lastHaptic = now;
  const run = kind === 'selection'
    ? Haptics.selectionChanged()
    : kind === 'success' || kind === 'warning' || kind === 'error'
      ? Haptics.notification({ type: { success: NotificationType.Success, warning: NotificationType.Warning, error: NotificationType.Error }[kind] })
      : Haptics.impact({ style: { light: ImpactStyle.Light, medium: ImpactStyle.Medium, heavy: ImpactStyle.Heavy }[kind] });
  run.catch(() => undefined);
}

window.APP_NATIVE = { isNative: native, haptic };

function hapticForTarget(target: Element): HapticKind | null {
  if (target.closest('.nav-photo')) return 'medium';
  if (target.closest('.nav-tab, [role="tab"], .d-toggle, [aria-pressed], input[type="checkbox"], input[type="radio"]')) return 'selection';
  if (target.closest('.d-btn-primary, button.bg-primary, button.bg-success, .today-modal-primary, .mmod-save-btn')) return 'light';
  return null;
}

if (hapticsOn) {
  document.addEventListener('click', event => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target || target.closest(':disabled, [aria-disabled="true"]')) return;
    const kind = hapticForTarget(target);
    if (kind) haptic(kind);
  }, { capture: true, passive: true });

  // toasts are the app's confirmation channel: success/error get a matching tick
  const watchToasts = () => {
    const container = document.getElementById('toast-container');
    if (!container) return;
    new MutationObserver(records => {
      for (const record of records) {
        for (const node of Array.from(record.addedNodes)) {
          if (!(node instanceof Element)) continue;
          if (node.classList.contains('toast-success')) haptic('success');
          else if (node.classList.contains('toast-error')) haptic('error');
          else if (node.classList.contains('toast-warning')) haptic('warning');
        }
      }
    }).observe(container, { childList: true });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', watchToasts);
  else watchToasts();
}

if (available('StatusBar')) {
  // Style.Dark = light text (for dark backgrounds), Style.Light = dark text
  const syncStatusBar = () => {
    const dark = document.body?.getAttribute('data-theme') === 'dark';
    StatusBar.setStyle({ style: dark ? Style.Dark : Style.Light }).catch(() => undefined);
  };
  const startStatusBar = () => {
    syncStatusBar();
    new MutationObserver(syncStatusBar).observe(document.body, { attributes: true, attributeFilter: ['data-theme'] });
  };
  if (document.body) startStatusBar();
  else document.addEventListener('DOMContentLoaded', startStatusBar);
}

if (available('Keyboard')) {
  Keyboard.addListener('keyboardWillShow', () => document.body?.classList.add('keyboard-open')).catch(() => undefined);
  Keyboard.addListener('keyboardWillHide', () => document.body?.classList.remove('keyboard-open')).catch(() => undefined);
}

export {};
